<template>
  <v-card class="my-2 px-4 py-3 v-card-customer" color="grey lighten-2" style="border-radius: 0.75rem" text>
    <v-layout class="subtitle-1">
      Active Filters
      <v-spacer></v-spacer>
      <v-btn slot="activator" color="grey" dark small text @click="clearAll">
        <v-icon class="mr-2">fa-times-circle</v-icon> clear all filters
      </v-btn>
    </v-layout>

    <abf-filter-chip
      v-for="filter in activeFilters"
      :key="filter.value"
      :label="filter.text"
      :icon-value="filter.icon"
      @remove="removeFilter(filter.type, filter.value)"
    ></abf-filter-chip>
    <abf-nullable-filter-chip v-if="ReferralPageFilters.referrer" :filter="referrerFilter" />
    <abf-nullable-filter-chip v-if="ReferralPageFilters.referrerType" :filter="referrerTypeFilter" />
    <abf-nullable-filter-chip v-if="ReferralPageFilters.legalStatus" :filter="legalStatusFilter" />
    <abf-nullable-filter-chip v-if="ReferralPageFilters.providerId" :filter="providerIdFilter" />
    <abf-nullable-filter-chip v-if="ReferralPageFilters.productId" :filter="productIdFilter" />
    <abf-nullable-filter-chip v-if="ReferralPageFilters.journeyId" :filter="journeyIdFilter" />
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AbfFilterChip from './AbfFilterChip';
import AbfNullableFilterChip from './AbfNullableFilterChip';

const avatarBaseUrl = 'https://s3-eu-west-1.amazonaws.com/media.alternativebusinessfunding.co.uk/public/avatars/';

export default {
  name: 'AbfActiveFilters',
  components: { AbfFilterChip, AbfNullableFilterChip },
  data() {
    return {
      filterDetails: [
        { type: 'filter', icon: 'fad fa-medal', text: 'Elite', value: 'eliteReferrer' },
        { type: 'filter', icon: 'fad fa-envelope', text: 'No email', value: 'noEmail' },
        { type: 'filter', icon: 'fad fa-envelope', text: 'With email', value: 'withEmail' },
        { type: 'filter', icon: 'fad fa-phone', text: 'With telephone', value: 'withPhone' },
        { type: 'filter', icon: 'fad fa-phone', text: 'No telephone', value: 'noPhone' },
        { type: 'filter', icon: 'fad fa-phone-plus', text: 'To be called', value: 'bool.toBeCalled' },
        { type: 'filter', icon: 'fad fa-user', text: 'Registered', value: 'registered' },
        { type: 'filter', icon: 'fad fa-user-slash', text: 'Not registered', value: 'notRegistered' },
        { type: 'filter', icon: 'fad fa-times', text: 'Closed', value: 'closed' },
        { type: 'filter', icon: 'fad fa-folder-open', text: 'Open', value: 'open' },
        { type: 'filter', icon: 'progress_full', text: 'Questions complete', value: 'questionsComplete' },
        { type: 'filter', icon: 'progress_empty', text: 'Questions incomplete', value: 'questionsIncomplete' },
        { type: 'filter', icon: 'fad fa-shipping-fast', text: 'Auto send enquiries', value: 'autoSendEnquiries' },
        { type: 'filter', icon: 'fad truck', text: 'Not auto send enquiries', value: 'notAutoSendEnquiries' },
        { type: 'filter', icon: 'fad fa-comment-alt-slash', text: 'No enquiries', value: 'noEnquiries' }
      ],
      referrerFilter: { icon: 'fad fa-people-carry', text: 'Referrer', value: 'referrer' },
      referrerTypeFilter: { icon: 'fad fa-people-carry', text: 'Referrer Type', value: 'referrerType' },
      legalStatusFilter: { icon: 'fad fa-people-carry', text: 'Legal Status', value: 'legalStatus' },
      providerIdFilter: { icon: 'fad fa-coins', text: 'Provider', value: 'providerId' },
      productIdFilter: { icon: 'fad fa-product-hunt', text: 'Product', value: 'productId' },
      journeyIdFilter: { icon: 'fad fa-road', text: 'Journey', value: 'journeyId' }
    };
  },
  computed: {
    ...mapGetters(['EnquiryStatuses', 'OwnersList', 'ReferralPageFilters']),
    activeFilters() {
      const ownerFilters = this.owners
        .filter(owner => this.ReferralPageFilters.owners.includes(owner.id))
        .map(owner => ({
          type: 'owner',
          icon: this.ownerAvatar(owner),
          text: owner.full_name,
          value: owner.id
        }));

      const enquiryFilters = this.EnquiryStatuses.filter(status =>
        this.ReferralPageFilters.enquiryStatuses.includes(status.id)
      ).map(status => ({ type: 'enquiry', icon: 'check_circle', text: status.name, value: status.id }));

      const otherFilters = this.filterDetails.filter(details => {
        if (details.value.startsWith('bool.')) {
          return this.ReferralPageFilters.bool[details.value.substring(5)] === true;
        }
        return this.ReferralPageFilters[details.value] === true;
      });

      return [].concat(otherFilters, enquiryFilters, ownerFilters);
    },
    owners() {
      let ownerFilterList = this.OwnersList.map(({ id, full_name, roles }) => ({
        id,
        full_name,
        roles
      }));
      ownerFilterList.push({
        id: null,
        full_name: 'Not Assigned',
        roles: []
      });
      return ownerFilterList;
    }
  },
  methods: {
    ...mapActions(['removeReferralEnquiryStatusFilter', 'removeReferralOwnerFilter', 'toggleReferralPageFilter']),
    clearAll() {
      this.$emit('reset');
    },
    removeFilter(type, value) {
      switch (type) {
        case 'filter':
          this.toggleReferralPageFilter(value);
          break;
        case 'enquiry':
          this.removeReferralEnquiryStatusFilter({ id: value });
          break;
        case 'owner':
          this.removeReferralOwnerFilter({ id: value });
          break;
      }
      this.$emit('change');
    },
    ownerAvatar(owner) {
      if (owner !== null && typeof owner !== 'undefined') {
        if (owner.roles.includes('Platform')) {
          return `${avatarBaseUrl}${owner.id}.png`;
        }
        if (owner.roles.includes('Call Centre')) {
          return 'fa-headset';
        }
        if (owner.roles.includes('Property Broker')) {
          return 'fa-user-hard-hat';
        }
      }
      return 'fa-user';
    }
  }
};
</script>

<style lang="scss" scoped>
div.input-group {
  padding-top: 0;
}
::v-deep .input-group__details {
  display: none;
}
</style>
